import { useState, useEffect, useRef, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart,
  MapPin,
  Ruler,
  Bed,
  Buildings,
  Stack,
  Target,
  Phone,
  WhatsappLogo,
  Eye,
  ArrowSquareOut,
  Warning,
  NotePencil,
  Copy,
} from "@phosphor-icons/react";
import { useFavorites } from "@/store/favoritesStore";
import { HeartColorPicker } from "@/components/HeartColorPicker";
import { useViewed } from "@/store/viewedStore";
import { useLang } from "@/i18n/LanguageContext";
import { getAccessToken, fetchAdjacentPropertyImage } from "@/lib/api";
import { fmtPrice, fmtDate } from "@/lib/format";
import { toWaNumber, toTelNumber } from "@/lib/phone";
import { EXTERNAL_LISTING_URL } from "@/lib/constants";
import { toast } from "sonner";
import { OwnerBadge } from "@/components/OwnerBadge";
import { ReportDialog } from "@/components/ReportDialog";
import { PropertyNoteDialog } from "@/components/PropertyNoteDialog";
import { CallPlanBtn, CallPlanStatus } from "@/components/CallPlanControls";
import { usePropertyNoteText } from "@/hooks/usePropertyNoteText";
import { useAuth } from "@/auth/AuthContext";
import { PhoneInfoBtn } from "@/components/PhoneInfoDialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

const isMobileDevice = () =>
  typeof window !== "undefined" && window.innerWidth < 768;

const isHouseType = (propertyType) => {
  const t = (propertyType || "").toLowerCase();
  return t.includes("fərdi") || t.includes("ferdi");
};

const copyToClipboard = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      ok ? resolve() : reject(new Error("copy failed"));
    } catch (err) {
      reject(err);
    }
  });
};

const getAreaInfo = (property) => {
  const type = (property?.propertyType || "").toLowerCase();
  const kv = property?.generalArea ? `${property.generalArea} m²` : null;
  const sot = property?.area ? `${property.area} sot` : null;

  const isLand = type.includes("torpaq");
  const isApartment = type.includes("mənzil") || type.includes("menzil");
  const isGarage = type.includes("qaraj");
  const isHouse =
    type.includes("fərdi") ||
    type.includes("ferdi") ||
    type.includes("bağ") ||
    type.includes("bag");
  const isNonResidential =
    type.includes("qeyri yaşayış") ||
    type.includes("qeyri yashayish") ||
    type.includes("qeyri yasayis");

  if (isLand) {
    return { kv: null, sot, primary: sot, isApartment };
  }

  if (isApartment || isGarage) {
    return { kv, sot: null, primary: kv, isApartment };
  }

  if (isHouse || isNonResidential) {
    return { kv, sot, primary: [kv, sot].filter(Boolean).join(", ") || null, isApartment };
  }

  return { kv, sot, primary: kv || sot || null, isApartment };
};

const CardNote = ({ text, lines = 3 }) => (
  <div
    data-testid="card-note-text"
    className="mt-1 mb-2 flex items-start gap-1.5 rounded-lg border border-[#EA580C]/30 bg-[#EA580C]/10 px-2.5 py-1.5 text-[11px] leading-snug text-foreground"
  >
    <NotePencil size={12} weight="fill" className="mt-0.5 shrink-0 text-[#EA580C]" />
    <p
      className="min-w-0 flex-1 break-words"
      style={
        lines === 1
          ? {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }
          : {
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: lines,
            overflow: "hidden",
            whiteSpace: "pre-wrap",
          }
      }
      title={text}
    >
      {text}
    </p>
  </div>
);

const ContactBtns = ({
  phone,
  propertyId,
  size = "sm",
  className = "",
  onUnauthorizedClick,
  onExpiredPackageClick,
  onNoteClick,
  hasActivePackage = true,
  hasNotes = false,
}) => {
  const tel = toTelNumber(phone || "");
  const wa = toWaNumber(phone || "");

  const stopBubble = (e) => {
    e.stopPropagation();
  };

  const S = size === "xs" ? 10 : 12;
  const box = size === "xs" ? "w-6 h-6" : "w-7 h-7";

  const checkAccess = () => {
    if (!getAccessToken()) {
      onUnauthorizedClick?.();
      return false;
    }

    if (!hasActivePackage) {
      onExpiredPackageClick?.();
      return false;
    }

    return true;
  };

  const handlePhone = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!checkAccess()) return;
    if (!tel) return;

    window.location.href = `tel:${tel}`;
  };

  const handleWa = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!checkAccess()) return;
    if (!wa) return;

    window.open(`https://wa.me/${wa}`, "_blank", "noopener,noreferrer");
  };

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!checkAccess()) return;
    if (!phone) return;

    copyToClipboard(phone)
      .then(() => toast.success("Nömrə kopyalandı!"))
      .catch(() => toast.error("Nömrəni kopyalamaq mümkün olmadı."));
  };

  const handleNote = (e) => {
    e.preventDefault();
    e.stopPropagation();

    onNoteClick?.();
  };

  return (
    <div
      className={`inline-flex items-center gap-1 ${className}`}
      onClick={stopBubble}
      onMouseDown={stopBubble}
    >
      <button
        type="button"
        onClick={handlePhone}
        data-testid="card-phone-btn"
        className={`${box} rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow-sm hover:brightness-110 transition`}
        aria-label="Zəng et"
        title="Zəng et"
      >
        <Phone size={S} weight="fill" />
      </button>

      <button
        type="button"
        onClick={handleWa}
        data-testid="card-whatsapp-btn"
        className={`${box} rounded-full btn-whatsapp flex items-center justify-center shadow-sm transition`}
        aria-label="WhatsApp"
        title="WhatsApp"
      >
        <WhatsappLogo size={S} weight="fill" />
      </button>

      <button
        type="button"
        onClick={handleCopy}
        data-testid="card-copy-btn"
        className={`${box} rounded-full bg-[#3B82F6] text-white flex items-center justify-center shadow-sm hover:bg-[#2563EB] transition`}
        aria-label="Nömrəni kopyala"
        title="Nömrəni kopyala"
      >
        <Copy size={S} weight="bold" />
      </button>
    

      {onNoteClick && getAccessToken() && (
        <button
          type="button"
          onClick={handleNote}
          data-testid="card-note-btn"
          className={`${box} rounded-full text-white flex items-center justify-center shadow-sm transition ${hasNotes
            ? "bg-[#C2410C] hover:bg-[#9A3412]"
            : "bg-[#FB923C] hover:bg-[#F97316]"
            }`}
          aria-label="Qeyd"
          title={hasNotes ? "Qeyd var — dəyiş" : "Qeyd əlavə et"}
        >
          <NotePencil size={S} weight="fill" />
        </button>
      )}
    </div>
  );
};

const GotoUrlBtn = ({
  url,
  size = "sm",
  className = "",
  onUnauthorizedClick,
  onExpiredPackageClick,
  hasActivePackage = true,
}) => {
  const S = size === "xs" ? 11 : 13;
  const box = size === "xs" ? "w-6 h-6" : "w-7 h-7";

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!getAccessToken()) {
      onUnauthorizedClick?.();
      return;
    }

    if (!hasActivePackage) {
      onExpiredPackageClick?.();
      return;
    }

    window.open(
      url || EXTERNAL_LISTING_URL,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="card-goto-url-btn"
      className={`${box} rounded-full bg-white text-slate-700 flex items-center justify-center shadow-md hover:bg-slate-100 transition ${className}`}
      aria-label="Mənbə saytda aç"
      title="Mənbə saytda aç"
    >
      <ArrowSquareOut size={S} weight="bold" />
    </button>
  );
};

export { ContactBtns };

const SWIPE_THRESHOLD = 50;

const SwipeableCardImage = ({
  property,
  imgUrl,
  setImgUrl,
  alt,
  className,
  detailUrl,
  onCardClick,
}) => {
  const [loading, setLoading] = useState(false);

  const dragging = useRef(false);
  const dragMoved = useRef(false);
  const startX = useRef(null);
  const pointerIdRef = useRef(null);

  const goTo = async (direction) => {
    if (loading) return;

    setLoading(true);
    try {
      const newUrl = await fetchAdjacentPropertyImage(
        property.idProperty,
        direction,
        imgUrl
      );
      if (newUrl) {
        setImgUrl(newUrl);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    dragMoved.current = false;
    startX.current = e.clientX;
    pointerIdRef.current = e.pointerId;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging.current || startX.current == null) return;
    if (Math.abs(e.clientX - startX.current) > 10) dragMoved.current = true;
  };

  const endDrag = (e) => {
    if (pointerIdRef.current != null) {
      e.currentTarget.releasePointerCapture?.(pointerIdRef.current);
      pointerIdRef.current = null;
    }
  };

  const onPointerUp = (e) => {
    if (!dragging.current || startX.current == null) {
      dragging.current = false;
      endDrag(e);
      return;
    }
    dragging.current = false;
    const deltaX = e.clientX - startX.current;
    startX.current = null;
    endDrag(e);

    if (Math.abs(deltaX) >= SWIPE_THRESHOLD) {
      if (deltaX < 0) goTo(1);
      else goTo(0);
    }
  };

  const onPointerCancel = (e) => {
    dragging.current = false;
    startX.current = null;
    endDrag(e);
  };

  const handleClick = (e) => {
    if (dragMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onCardClick?.(e);
  };

  const handleDragStart = (e) => {
    e.preventDefault();
  };

  return (
    <Link
      to={detailUrl}
      target={isMobileDevice() ? undefined : "_blank"}
      rel={isMobileDevice() ? undefined : "noopener noreferrer"}
      onClick={handleClick}
      draggable={false}
      onDragStart={handleDragStart}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      className="block w-full h-full relative touch-pan-y select-none"
      data-testid="card-image-swiper"
    >
      <img
        src={imgUrl}
        alt={alt}
        className={className}
        loading="lazy"
        draggable={false}
      />

      {loading && (
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </Link>
  );
};

const PropertyCardImpl = ({
  property,
  layout = "grid",
  hasActivePackage: propHasActivePackage,
  showNote = false,
  showCallPlan = false,
}) => {
  const { t } = useLang();
  const nav = useNavigate();
  const auth = useAuth();

  const hasActivePackage = propHasActivePackage ?? Boolean(auth?.isPackageActive || auth?.hasActivePackage);

  const favBtnRef = useRef(null);

  const getColorFn = useFavorites((s) => s.getColor);
  const setColorFn = useFavorites((s) => s.setColor);

  const favColor = getAccessToken() ? getColorFn(property) : null;
  const isFav = !!favColor;

  const [showColorPicker, setShowColorPicker] = useState(false);

  const isViewedFn = useViewed((s) => s.isViewed);
  const viewed =
    Boolean(property.isTracked) ||
    isViewedFn(property.idProperty);

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showFavLoginPrompt, setShowFavLoginPrompt] = useState(false);
  const [showContactLoginPrompt, setShowContactLoginPrompt] = useState(false);
  const [showReportLoginPrompt, setShowReportLoginPrompt] = useState(false);
  const [showPaymentPrompt, setShowPaymentPrompt] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [hasNotes, setHasNotes] = useState(Boolean(property.hasNotes));

  useEffect(() => {
    setHasNotes(Boolean(property.hasNotes));
  }, [property.hasNotes]);

  const { data: fetchedNote } = usePropertyNoteText(
    property.idProperty,
    showNote && hasNotes
  );
  const noteText = showNote && hasNotes ? fetchedNote || "" : "";

  const {
    region,
    metro,
    room,
    operationType: op,
    propertyType: propType,
    ownerType,
    target,
  } = property;

  const areaInfo = getAreaInfo(property);
  const houseFloor = isHouseType(propType);
  const opRent = op?.toLowerCase().includes("kir") || op?.toLowerCase().includes("günl");

  const [heroImg, setHeroImg] = useState(
    property.image ||
    "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&auto=format&fit=crop"
  );

  const detailUrl = `/detail/${property.propertyCode}`;

  const onFav = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!getAccessToken()) {
      setShowFavLoginPrompt(true);
      return;
    }

    setShowColorPicker((v) => !v);
  };

  const onSelectColor = (color) => {
    setColorFn(property, color);
    toast(
      color
        ? t("common.addedToFavorites")
        : t("common.removedFromFavorites")
    );
    setShowColorPicker(false);
  };

  const onCardClick = (e) => {
    if (isMobileDevice()) {
      return;
    }

    e.preventDefault();
    window.open(detailUrl, "_blank", "noopener,noreferrer");
  };

  const confirmGoToLogin = () => {
    setShowLoginPrompt(false);

    if (isMobileDevice()) {
      nav(detailUrl);
      return;
    }

    window.open(detailUrl, "_blank", "noopener,noreferrer");
  };

  const cancelGoToLogin = () => {
    setShowLoginPrompt(false);
  };

  const onReport = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!getAccessToken()) {
      setShowReportLoginPrompt(true);
      return;
    }

    setReportOpen(true);
  };

  const onOpenNote = () => {
    if (!getAccessToken()) {
      setShowContactLoginPrompt(true);
      return;
    }

    setNoteOpen(true);
  };

  const confirmFavLogin = () => {
    setShowFavLoginPrompt(false);
    nav("/login");
  };

  const confirmReportLogin = () => {
    setShowReportLoginPrompt(false);
    nav("/login");
  };

  const handleUnauthorizedClick = () => {
    setShowContactLoginPrompt(true);
  };

  const handleExpiredPackageClick = () => {
    setShowPaymentPrompt(true);
  };

  const confirmContactLogin = () => {
    setShowContactLoginPrompt(false);
    nav("/login");
  };

  const confirmPayment = () => {
    setShowPaymentPrompt(false);
    nav("/payments?autoOpen=true");
  };

  const loginPrompt = (
    <AlertDialog
      open={showLoginPrompt}
      onOpenChange={setShowLoginPrompt}
    >
      <AlertDialogContent data-testid="login-required-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Giriş tələb olunur
          </AlertDialogTitle>

          <AlertDialogDescription>
            Sistemdən daha rahat istifadə üçün şəxsi kabinetidən istifadə tələb edilir.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="login-required-cancel"
            onClick={cancelGoToLogin}
          >
            Xeyr
          </AlertDialogCancel>

          <AlertDialogAction
            data-testid="login-required-confirm"
            onClick={confirmGoToLogin}
          >
            Davam et
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const contactLoginPrompt = (
    <AlertDialog
      open={showContactLoginPrompt}
      onOpenChange={setShowContactLoginPrompt}
    >
      <AlertDialogContent data-testid="contact-login-required-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Giriş tələb olunur
          </AlertDialogTitle>

          <AlertDialogDescription>
            Sistemdən daha rahat istifadə üçün şəxsi kabinetidən istifadə tələb edilir.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="contact-login-cancel">
            Xeyr
          </AlertDialogCancel>

          <AlertDialogAction
            data-testid="contact-login-confirm"
            onClick={confirmContactLogin}
          >
            Davam et
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const paymentPrompt = (
    <AlertDialog
      open={showPaymentPrompt}
      onOpenChange={setShowPaymentPrompt}
    >
      <AlertDialogContent data-testid="payment-required-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            İstifadə paketinizin müddəti bitmişdir
          </AlertDialogTitle>

          <AlertDialogDescription>
            Nömrəni görmək, kopyalamaq, WhatsApp vasitəsilə əlaqə saxlamaq və ya mənbə keçidinə daxil olmaq üçün zəhmət olmasa ödəniş edərək paketinizi yeniləyin
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="payment-required-cancel">
            Bağla
          </AlertDialogCancel>

          <AlertDialogAction
            data-testid="payment-required-confirm"
            onClick={confirmPayment}
          >
            Ödəniş et
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const favLoginPrompt = (
    <AlertDialog
      open={showFavLoginPrompt}
      onOpenChange={setShowFavLoginPrompt}
    >
      <AlertDialogContent data-testid="fav-login-required-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Giriş tələb olunur
          </AlertDialogTitle>

          <AlertDialogDescription>
            Seçilmişlərə əlavə etmək üçün daxil olun.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="fav-login-required-cancel"
          >
            Ləğv et
          </AlertDialogCancel>

          <AlertDialogAction
            data-testid="fav-login-required-confirm"
            onClick={confirmFavLogin}
          >
            Daxil ol
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const reportLoginPrompt = (
    <AlertDialog
      open={showReportLoginPrompt}
      onOpenChange={setShowReportLoginPrompt}
    >
      <AlertDialogContent data-testid="report-login-required-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Giriş tələb olunur
          </AlertDialogTitle>

          <AlertDialogDescription>
            Şikayət etmək üçün hesabınıza daxil olmalısınız.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>
            Ləğv et
          </AlertDialogCancel>

          <AlertDialogAction onClick={confirmReportLogin}>
            Daxil ol
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (layout === "list") {
    return (
      <>
        <div
          data-testid={`property-card-${property.idProperty}`}
          className={`group bg-card rounded-2xl border border-border/60 p-2.5 flex flex-col sm:flex-row gap-4 hover:shadow-md transition-all duration-200 relative items-center ${viewed ? "opacity-80" : ""
            }`}
        >
          <div className="relative w-full sm:w-52 h-36 shrink-0 rounded-xl overflow-hidden">
            <SwipeableCardImage
              property={property}
              imgUrl={heroImg}
              setImgUrl={setHeroImg}
              alt={property.title || "Property"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              detailUrl={detailUrl}
              onCardClick={onCardClick}
            />

            {op && (
              <span className={`absolute top-2 left-2 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded text-white shadow-sm ${opRent ? "bg-amber-500" : "bg-emerald-600"}`}>
                {op}
              </span>
            )}

            {property.propertyDate && (
              <span className="absolute top-8 left-2 text-[9px] sm:text-[10px] font-medium px-1.5 sm:px-2 py-0.5 rounded bg-slate-900/70 text-white shadow-sm">
                {fmtDate(property.propertyDate)}
              </span>
            )}

            <div className="absolute top-2 right-2 flex items-center gap-1">
              <CallPlanBtn property={property} size="w-6 h-6 sm:w-7 sm:h-7" />

              <button
                type="button"
                onClick={onReport}
                data-testid="card-report-btn"
                className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white text-slate-700 hover:bg-slate-100 flex items-center justify-center shadow-md transition"
                title="Şikayət et"
              >
                <Warning size={12} weight="bold" />
              </button>

              <div className="relative">
                <button
                  ref={favBtnRef}
                  type="button"
                  onClick={onFav}
                  data-testid="card-fav-btn"
                  className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white text-slate-700 hover:bg-slate-100 flex items-center justify-center shadow-md transition"
                >
                  <Heart
                    size={13}
                    weight={isFav ? "fill" : "regular"}
                    style={isFav ? { color: favColor } : undefined}
                  />
                </button>
                {showColorPicker && (
                  <HeartColorPicker
                    activeColor={favColor}
                    onSelect={onSelectColor}
                    onClose={() => setShowColorPicker(false)}
                    anchorRef={favBtnRef}
                  />
                )}
              </div>
            </div>

            {viewed && (
              <span className="absolute top-14 left-2 text-[9px] sm:text-[10px] font-medium bg-black/60 text-white px-1.5 sm:px-2 py-0.5 rounded backdrop-blur-sm flex items-center gap-1">
                <Eye size={10} />
                Baxılıb
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0 flex flex-col justify-between h-full py-1 w-full">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-bold text-xs uppercase text-slate-500 tracking-wide">
                  {(propType || "ƏMLAK").toUpperCase()} · #
                  {property.idProperty || property.propertyCode}
                </span>

                {ownerType && (
                  <OwnerBadge ownerType={ownerType} />
                )}
              </div>

              <div className="text-emerald-600 font-bold text-xl mb-2">
                {fmtPrice(property.price)}{" "}
                <span className="text-sm font-semibold">
                  AZN
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 font-medium mb-3">
                {room && (
                  <span className="flex items-center gap-1">
                    <Bed
                      size={14}
                      className="text-slate-400"
                    />
                    {room}
                  </span>
                )}

                {areaInfo.primary && (
                  <span className="flex items-center gap-1">
                    <Ruler
                      size={14}
                      className="text-slate-400"
                    />
                    {areaInfo.primary}
                  </span>
                )}

                {Boolean(property.floor) && (
                  <span className="flex items-center gap-1">
                    <Buildings size={14} className="text-slate-400" />
                    {houseFloor ? property.floor : `${property.floor}/${property.floorOf || "-"}`} mər.
                  </span>
                )}
                {areaInfo.isApartment && property.buildingType && (
                  <span className="flex items-center gap-1">
                    <Stack size={14} className="text-slate-400" />
                    {property.buildingType}
                  </span>
                )}
                {target && (
                  <span className="flex items-center gap-1">
                    <Target size={14} className="text-slate-400" />
                    {target}
                  </span>
                )}
              </div>

              {noteText && <CardNote text={noteText} lines={1} />}
            </div>

            <div className="flex flex-col gap-2">
              {showCallPlan && <CallPlanStatus property={property} />}

              <div className="flex items-center gap-1 text-xs text-slate-500 min-w-0">
                <MapPin
                  size={13}
                  weight="bold"
                  className="shrink-0 text-slate-400"
                />

                <span className="truncate">
                  {[region, metro].filter(Boolean).join(", ") ||
                    "Bakı ş."}
                </span>
              </div>

              <div className="flex items-center justify-end gap-2 flex-wrap">
                <ContactBtns
                  className="shrink-0"
                  hasNotes={hasNotes}
                  phone={property.cpPhoneNumber01}
                  size="xs"
                  hasActivePackage={hasActivePackage}
                  propertyId={property.idProperty}
                  onUnauthorizedClick={handleUnauthorizedClick}
                  onExpiredPackageClick={handleExpiredPackageClick}
                  onNoteClick={onOpenNote}
                />

                <GotoUrlBtn
                  url={property.url}
                  size="sm"
                  hasActivePackage={hasActivePackage}
                  onUnauthorizedClick={handleUnauthorizedClick}
                  onExpiredPackageClick={handleExpiredPackageClick}
                />
              </div>
            </div>
          </div>
        </div>

        {loginPrompt}
        {contactLoginPrompt}
        {paymentPrompt}
        {favLoginPrompt}
        {reportLoginPrompt}

        <ReportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          propertyId={property.idProperty}
        />

        <PropertyNoteDialog
          open={noteOpen}
          onOpenChange={setNoteOpen}
          onNoteChange={setHasNotes}
          propertyId={property.idProperty}
        />
      </>
    );
  }

  return (
    <>
      <div
        data-testid={`property-card-${property.idProperty}`}
        className={`group bg-card rounded-2xl border border-border/60 overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col relative ${viewed ? "opacity-85" : ""
          }`}
      >
        <div className="relative aspect-[4/3] w-full overflow-hidden">
          <SwipeableCardImage
            property={property}
            imgUrl={heroImg}
            setImgUrl={setHeroImg}
            alt={property.title || "Property"}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            detailUrl={detailUrl}
            onCardClick={onCardClick}
          />

          {op && (
            <span className={`absolute top-2 left-2 sm:top-2.5 sm:left-2.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded text-white shadow-md ${opRent ? "bg-amber-500" : "bg-emerald-600"}`}>
              {op}
            </span>
          )}

          {property.propertyDate && (
            <span className="absolute top-8 left-2 sm:top-9 sm:left-2.5 text-[9px] sm:text-[10px] font-medium px-1.5 sm:px-2 py-0.5 rounded bg-slate-900/70 text-white shadow-sm">
              {fmtDate(property.propertyDate)}
            </span>
          )}

          <div className="absolute top-2 right-2 sm:top-2.5 sm:right-2.5 flex items-center gap-1">
            <CallPlanBtn property={property} size="w-6 h-6 sm:w-7 sm:h-7" />

            <button
              type="button"
              onClick={onReport}
              data-testid="card-report-btn"
              className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white text-slate-700 hover:bg-slate-100 flex items-center justify-center shadow-md transition"
              title="Şikayət et"
            >
              <Warning size={12} weight="bold" />
            </button>

            <div className="relative">
              <button
                ref={favBtnRef}
                type="button"
                onClick={onFav}
                data-testid="card-fav-btn"
                className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white text-slate-700 hover:bg-slate-100 flex items-center justify-center shadow-md transition"
              >
                <Heart
                  size={13}
                  weight={isFav ? "fill" : "regular"}
                  style={isFav ? { color: favColor } : undefined}
                />
              </button>
              {showColorPicker && (
                <HeartColorPicker
                  activeColor={favColor}
                  onSelect={onSelectColor}
                  onClose={() => setShowColorPicker(false)}
                  anchorRef={favBtnRef}
                />
              )}
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-2.5 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end justify-between pointer-events-none">
            <span className="text-white font-bold text-base tracking-tight drop-shadow-sm">
              {fmtPrice(property.price)}{" "}
              <span className="text-xs font-normal opacity-90">
                AZN
              </span>
            </span>

            <div className="pointer-events-auto">
              <GotoUrlBtn
                url={property.url}
                size="sm"
                hasActivePackage={hasActivePackage}
                onUnauthorizedClick={handleUnauthorizedClick}
                onExpiredPackageClick={handleExpiredPackageClick}
              />
            </div>
          </div>

          {viewed && (
            <span className="absolute top-14 left-2 sm:left-2.5 text-[9px] sm:text-[10px] font-medium bg-black/60 text-white px-1.5 sm:px-2 py-0.5 rounded backdrop-blur-sm flex items-center gap-1">
              <Eye size={10} />
              Baxılıb
            </span>
          )}
        </div>

        <div className="p-3 flex-1 flex flex-col justify-between bg-card">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-1 mb-2">
              <span className="font-bold text-[11px] uppercase text-slate-500 tracking-wider truncate">
                {(propType ||
                  property.title ||
                  "ƏMLAK").toUpperCase()}{" "}
                · #{property.idProperty || property.propertyCode}
              </span>

              {ownerType && (
                <OwnerBadge ownerType={ownerType} />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2.5 text-xs text-slate-600 font-medium mb-2">
              {room && (
                <span className="flex items-center gap-1">
                  <Bed
                    size={14}
                    className="text-slate-400"
                  />
                  {room ? room : '-'}
                </span>
              )}

              {areaInfo.primary && (
                <span className="flex items-center gap-1">
                  <Ruler
                    size={14}
                    className="text-slate-400"
                  />
                  {areaInfo.primary ? areaInfo.primary : '-'}
                </span>
              )}

              {Boolean(property.floor) && (
                <span className="flex items-center gap-1">
                  <Buildings size={14} className="text-slate-400" />
                  {houseFloor ? property.floor : `${property.floor}/${property.floorOf || "-"}`} mər.
                </span>
              )}
              {areaInfo.isApartment && property.buildingType && (
                <span className="flex items-center gap-1">
                  <Stack size={14} className="text-slate-400" />
                  {property.buildingType}
                </span>
              )}
              {target && (
                <span className="flex items-center gap-1">
                  <Target size={14} className="text-slate-400" />
                  {target}
                </span>
              )}
            </div>

            {noteText && <CardNote text={noteText} lines={3} />}
          </div>

          <div className="flex flex-col gap-2 pt-2 mt-1 border-t border-border/40">
            {showCallPlan && <CallPlanStatus property={property} />}

            <div className="flex items-center gap-1 text-xs text-slate-500 min-w-0">
              <MapPin
                size={13}
                weight="bold"
                className="shrink-0 text-slate-400"
              />

              <span className="truncate">
                {[region, metro].filter(Boolean).join(", ") ||
                  "Bakı ş."}
              </span>
            </div>

            <div className="flex items-center justify-end">
              <ContactBtns
                className="shrink-0"
                hasNotes={hasNotes}
                phone={property.cpPhoneNumber01}
                size="xs"
                hasActivePackage={hasActivePackage}
                propertyId={property.idProperty}
                onUnauthorizedClick={handleUnauthorizedClick}
                onExpiredPackageClick={handleExpiredPackageClick}
                onNoteClick={onOpenNote}
              />
            </div>
          </div>
        </div>
      </div>

      {loginPrompt}
      {contactLoginPrompt}
      {paymentPrompt}
      {favLoginPrompt}
      {reportLoginPrompt}

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        propertyId={property.idProperty}
      />

      <PropertyNoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        onNoteChange={setHasNotes}
        propertyId={property.idProperty}
      />
    </>
  );
};

export const PropertyCard = memo(PropertyCardImpl);